import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import utils from "../../../universal/utils";
import adminFetch from "../../../universal/adminFetch";
import useIsMountedRef from "../../../hooks/useIsMountedRef";

// Representative IANA timezone for each region — used for DST-aware display only.
// These match the timezone the majority of that region's players experience.
const REGIONS = [
  { key: 'eume', label: 'Europe & Middle East', ianaTimezone: 'Europe/Paris' },
  { key: 'na',   label: 'North America',        ianaTimezone: 'America/New_York' },
  { key: 'sa',   label: 'South America',        ianaTimezone: 'America/Sao_Paulo' },
  { key: 'apac', label: 'Asia-Pacific',         ianaTimezone: 'Asia/Tokyo' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => i);
function padHour(h) { return String(h).padStart(2, '0') + ':00'; }

/**
 * Convert a UTC hour integer to a formatted local time string for the given
 * IANA timezone, fully DST-aware via the browser's Intl API.
 */
function formatUtcHourInZone(utcHour, ianaTimezone) {
  const d = new Date();
  d.setUTCHours(utcHour, 0, 0, 0);
  return d.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: ianaTimezone,
  });
}

/**
 * Return a short DST-aware label like "CEST (UTC+2)" for a given IANA timezone
 * at the current moment. Uses Intl — never hardcoded offsets.
 */
function getTzLabel(ianaTimezone) {
  const now = new Date();
  const shortName = new Intl.DateTimeFormat('en-GB', {
    timeZone: ianaTimezone,
    timeZoneName: 'short',
  }).formatToParts(now).find(p => p.type === 'timeZoneName')?.value ?? ianaTimezone;

  const offsetStr = new Intl.DateTimeFormat('en-GB', {
    timeZone: ianaTimezone,
    timeZoneName: 'shortOffset',
  }).formatToParts(now).find(p => p.type === 'timeZoneName')?.value?.replace('GMT', 'UTC') ?? '';

  return `${shortName} (${offsetStr})`;
}

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  regionCard: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    background: 'rgba(255,255,255,0.03)',
    borderRadius: 6,
    border: '1px solid rgba(255,255,255,0.08)',
  },
  regionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  inputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  inputField: {
    width: 160,
  },
  actionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    flexWrap: 'wrap',
  },
  status: {
    marginTop: theme.spacing(1),
  },
}));

function buildDefaultSchedule() {
  const schedule = { enabled: false };
  REGIONS.forEach(({ key }) => {
    schedule[key] = { openHourUTC: 0, closeHourUTC: 23, enabled: true };
  });
  return schedule;
}

export default function QueueScheduleSettings({ userId }) {
  const classes = useStyles();
  const isMountedRef = useIsMountedRef();
  const basePath = useMemo(() => utils.getEdgeBasePath(), []);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [schedule, setSchedule] = useState(buildDefaultSchedule);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await adminFetch(`${basePath}/api/v7/admin/settings/queue-schedule/adpass/${userId}`);
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.err || 'Failed to load queue schedule');
      if (!isMountedRef.current) return;
      const s = data.schedule || buildDefaultSchedule();
      setSchedule(s);
    } catch (e) {
      if (isMountedRef.current) setError(e?.message || String(e));
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [basePath, isMountedRef, userId]);

  useEffect(() => { load(); }, [load]);

  const save = useCallback(async () => {
    if (!userId) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await adminFetch(`${basePath}/api/v7/admin/settings/queue-schedule/adpass/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ schedule }),
      });
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.err || 'Failed to save queue schedule');
      if (!isMountedRef.current) return;
      if (data.schedule) setSchedule(data.schedule);
      setSuccess('Queue schedule saved.');
    } catch (e) {
      if (isMountedRef.current) setError(e?.message || String(e));
    } finally {
      if (isMountedRef.current) setSaving(false);
    }
  }, [basePath, isMountedRef, schedule, userId]);

  const updateRegionField = useCallback((regionKey, field, value) => {
    setSchedule((prev) => ({
      ...prev,
      [regionKey]: { ...prev[regionKey], [field]: value },
    }));
  }, []);

  const toggleMasterEnabled = useCallback(() => {
    setSchedule((prev) => ({ ...prev, enabled: !prev.enabled }));
  }, []);

  return (
    <Paper className={classes.paper}>
      <Box className={classes.headerRow}>
        <Box>
          <Typography variant="h6">Queue Schedule</Typography>
          <Typography variant="body2" color="textSecondary">
            Configure per-region queue open/close hours (UTC). Regions outside their schedule window will reject queue joins.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setOpen((v) => !v)}
          startIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        >
          {open ? 'Hide' : 'Show'}
        </Button>
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box className={classes.actionRow}>
          <Typography variant="body2">Master schedule enabled:</Typography>
          <Switch
            checked={!!schedule.enabled}
            onChange={toggleMasterEnabled}
            color="primary"
            disabled={loading || saving}
          />
          <Typography variant="body2" style={{ fontWeight: 600, color: schedule.enabled ? '#1b5e20' : '#b71c1c' }}>
            {schedule.enabled ? 'ON' : 'OFF'}
          </Typography>
        </Box>

        {REGIONS.map(({ key, label, ianaTimezone }) => {
          const region = schedule[key] || { openHourUTC: 0, closeHourUTC: 23, enabled: true };
          const localOpen = formatUtcHourInZone(region.openHourUTC, ianaTimezone);
          const localClose = formatUtcHourInZone(region.closeHourUTC, ianaTimezone);
          const tzLabel = getTzLabel(ianaTimezone);

          return (
            <Box key={key} className={classes.regionCard}>
              <Box className={classes.regionHeader}>
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                  {label} ({key.toUpperCase()})
                </Typography>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Typography variant="body2">Enabled:</Typography>
                  <Switch
                    checked={!!region.enabled}
                    onChange={() => updateRegionField(key, 'enabled', !region.enabled)}
                    size="small"
                    color="primary"
                    disabled={loading || saving}
                  />
                </Box>
              </Box>

              <Box className={classes.inputRow}>
                <FormControl variant="outlined" size="small" className={classes.inputField} disabled={loading || saving}>
                  <InputLabel>Open hour (UTC)</InputLabel>
                  <Select
                    label="Open hour (UTC)"
                    value={region.openHourUTC}
                    onChange={(e) => updateRegionField(key, 'openHourUTC', e.target.value)}
                  >
                    {HOURS.map((h) => (
                      <MenuItem key={h} value={h}>{padHour(h)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl variant="outlined" size="small" className={classes.inputField} disabled={loading || saving}>
                  <InputLabel>Close hour (UTC)</InputLabel>
                  <Select
                    label="Close hour (UTC)"
                    value={region.closeHourUTC}
                    onChange={(e) => updateRegionField(key, 'closeHourUTC', e.target.value)}
                  >
                    {HOURS.map((h) => (
                      <MenuItem key={h} value={h}>{padHour(h)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="body2" color="textSecondary">
                  {tzLabel}: {localOpen} – {localClose}
                </Typography>
              </Box>
            </Box>
          );
        })}

        <Box className={classes.actionRow}>
          <Button
            variant="contained"
            color="primary"
            onClick={save}
            disabled={loading || saving || !userId}
          >
            {saving ? <CircularProgress size={18} /> : 'Save Schedule'}
          </Button>
          <Button
            variant="outlined"
            onClick={load}
            disabled={loading || saving || !userId}
          >
            Refresh
          </Button>
        </Box>

        {loading && (
          <Typography className={classes.status} variant="body2" color="textSecondary">
            Loading…
          </Typography>
        )}
        {error && (
          <Typography className={classes.status} variant="body2" color="error">
            {error}
          </Typography>
        )}
        {success && (
          <Typography className={classes.status} variant="body2" style={{ color: '#1b5e20' }}>
            {success}
          </Typography>
        )}
      </Collapse>
    </Paper>
  );
}
